import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { authHeaders, StatusChip, fmtDate, todayISO, Pager, openPrintWindow, escHtml } from './instrShared';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Spinner, Plus, Eye, Flask, Printer } from '@phosphor-icons/react';
import { toast } from 'sonner';

const PAGE_SIZE = 8;

const reqLabel = (p) => {
  if (p.requirement_type === 'range') return `${p.min_value} – ${p.max_value}`;
  if (p.requirement_type === 'max') return `≤ ${p.max_value}`;
  if (p.requirement_type === 'nil') return 'NIL';
  if (p.requirement_type === 'absent') return 'Absent';
  return '—';
};

function printQualityReport(t) {
  const rows = (t.results || []).map((r) => `
    <tr>
      <td>${escHtml(r.parameter)}</td>
      <td>${escHtml(r.category)}</td>
      <td>${escHtml(reqLabel(r))}</td>
      <td>${(r.requirement_type === 'range' || r.requirement_type === 'max') ? escHtml(r.value) : (r.detected ? 'Detected' : 'Not detected')}</td>
      <td class="${r.passed ? 'pass' : 'fail'}">${r.passed ? 'PASS' : 'FAIL'}</td>
    </tr>`).join('');
  openPrintWindow(`Quality Report ${t.report_number || ''}`, `
    <h1>Water Quality Test Report</h1>
    <p class="sub">Ecowater Cloud · Instrumentation Operations</p>
    <div class="meta">
      <span><b>Report No:</b> ${escHtml(t.report_number || '—')}</span>
      <span><b>Sample Date:</b> ${escHtml(fmtDate(t.sample_date))}</span>
      <span><b>Store:</b> ${escHtml(t.store_name || '—')}</span>
      <span><b>Tested By:</b> ${escHtml(t.tested_by)}</span>
      <span><b>Overall Result:</b> <span class="${t.overall_pass ? 'pass' : 'fail'}">${t.overall_pass ? 'PASS' : `FAIL (${t.failed_count})`}</span></span>
      ${t.comment ? `<span><b>Comment:</b> ${escHtml(t.comment)}</span>` : ''}
    </div>
    <table>
      <thead><tr><th>Parameter</th><th>Category</th><th>Requirement</th><th>Result</th><th>Status</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <p class="footer">Generated by Ecowater Cloud on ${escHtml(new Date().toLocaleString())}</p>
  `);
}

export const InstrQualityTab = () => {
  const [stores, setStores] = useState([]);
  const [tests, setTests] = useState([]);
  const [params, setParams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [viewTest, setViewTest] = useState(null);
  const [form, setForm] = useState({ store_id: '', sample_date: todayISO(), comment: '', entries: {} });
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/instrumentation/stores`, { headers: authHeaders() }),
      axios.get(`${API_URL}/api/instrumentation/quality-parameters`, { headers: authHeaders() }),
    ])
      .then(([s, p]) => {
        setStores(Array.isArray(s.data) ? s.data : []);
        setParams(Array.isArray(p.data) ? p.data : []);
      })
      .catch((e) => toast.error(formatApiError(e, 'Failed to load quality data')));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/instrumentation/quality-tests?limit=200`, { headers: authHeaders() });
      setTests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load quality tests'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const entries = {};
    params.forEach((p) => {
      entries[p.id] = (p.requirement_type === 'nil' || p.requirement_type === 'absent')
        ? { detected: false }
        : { value: '' };
    });
    setForm({ store_id: stores.length === 1 ? stores[0].id : '', sample_date: todayISO(), comment: '', entries });
    setNewOpen(true);
  };

  const setEntry = (id, patch) => setForm((prev) => ({
    ...prev, entries: { ...prev.entries, [id]: { ...prev.entries[id], ...patch } },
  }));

  const submit = async () => {
    if (!form.store_id) { toast.error('Select a store'); return; }
    const results = [];
    for (const p of params) {
      const e = form.entries[p.id] || {};
      if (p.requirement_type === 'range' || p.requirement_type === 'max') {
        if (e.value === '' || e.value === undefined) { toast.error(`Enter a value for ${p.parameter}`); return; }
        results.push({ parameter_id: p.id, value: parseFloat(e.value) });
      } else {
        results.push({ parameter_id: p.id, detected: !!e.detected });
      }
    }
    if (results.length === 0) { toast.error('No quality parameters configured yet'); return; }
    setSaving(true);
    try {
      const store = stores.find((s) => s.id === form.store_id);
      const { data } = await axios.post(`${API_URL}/api/instrumentation/quality-tests`, {
        store_id: form.store_id,
        store_name: store?.name || '',
        sample_date: form.sample_date,
        comment: form.comment,
        results,
      }, { headers: authHeaders() });
      if (data.overall_pass) toast.success(`Report ${data.report_number} saved — all parameters PASSED`);
      else toast.warning(`Report ${data.report_number} saved — ${data.failed_count} parameter(s) FAILED`);
      setNewOpen(false);
      setPage(1);
      load();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to record test'));
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(tests.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = tests.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="space-y-5" data-testid="instr-quality-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
            <Flask size={16} weight="fill" />
          </span>
          <p className="text-[13px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            Quality Test Reports
          </p>
        </div>
        <Button
          onClick={openNew}
          disabled={stores.length === 0 || params.length === 0}
          className="h-9 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
          data-testid="qc-new-test-btn"
        >
          <Plus size={14} className="mr-2" /> New Report
        </Button>
      </div>

      {params.length === 0 && !loading && (
        <p className="text-xs text-[#A16207] bg-[#FEF3C7] rounded-lg px-3 py-2">
          No quality parameters configured. Ask a Super Admin to add them under Configurations → Instrumentation.
        </p>
      )}

      {loading ? (
        <div className="py-20 text-center"><Spinner size={22} className="animate-spin text-[#0A2540] inline" /></div>
      ) : (
        <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden" data-testid="qc-tests-list">
          <div className="grid grid-cols-[120px_150px_1fr_1fr_60px] gap-2 items-center px-4 py-2.5 bg-[#FCFAF6] text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">
            <span>Date</span>
            <span>Report Number</span>
            <span>Store Name</span>
            <span>Tester</span>
            <span />
          </div>
          {tests.length === 0 ? (
            <p className="text-xs text-[#78716C] py-10 text-center">No quality tests recorded yet.</p>
          ) : (
            <div className="divide-y divide-[#EDE6D8]">
              {paged.map((t) => (
                <div key={t.id} className="grid grid-cols-[120px_150px_1fr_1fr_60px] gap-2 items-center px-4 py-3" data-testid={`qc-test-${t.id}`}>
                  <span className="text-xs font-mono text-[#78716C]">{fmtDate(t.sample_date)}</span>
                  <span className="text-sm font-mono font-semibold text-[#0A2540]">{t.report_number || '—'}</span>
                  <span className="text-sm text-[#1C1917] truncate">{t.store_name || '—'}</span>
                  <span className="text-sm text-[#78716C] truncate">{t.tested_by}</span>
                  <button
                    type="button"
                    onClick={() => setViewTest(t)}
                    className="text-[#0A2540] hover:bg-[#0A2540]/5 rounded-md p-1.5 transition justify-self-end"
                    title="View report"
                    data-testid={`qc-view-${t.id}`}
                  >
                    <Eye size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
          <Pager page={safePage} totalPages={totalPages} count={tests.length} label="reports" onPage={setPage} testid="qc-pagination" />
        </div>
      )}

      {/* New report dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="qc-new-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flask size={18} weight="duotone" /> New Quality Test Report</DialogTitle>
            <DialogDescription>Record readings for a store. Results are auto-checked against conformity requirements and a report number is generated on save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Store</span>
                <Select value={form.store_id} onValueChange={(v) => setForm((p) => ({ ...p, store_id: v }))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="qc-dialog-store">
                    <SelectValue placeholder="Select store…" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.campus_name ? ` · ${s.campus_name}` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Sample Date</span>
                <Input
                  type="date"
                  max={todayISO()}
                  value={form.sample_date}
                  onChange={(e) => setForm((p) => ({ ...p, sample_date: e.target.value }))}
                  className="h-9 text-sm"
                  data-testid="qc-dialog-date"
                />
              </div>
            </div>
            <div className="space-y-1">
              <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">Comment (optional)</span>
              <Input
                value={form.comment}
                onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
                placeholder="e.g. Post-filter sample"
                className="h-9 text-sm"
                data-testid="qc-dialog-comment"
              />
            </div>

            <div className="border border-[#EDE6D8] rounded-lg divide-y divide-[#EDE6D8]">
              {params.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`qc-param-${p.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1C1917] truncate">{p.parameter}</p>
                    <p className="text-[10px] font-mono text-[#78716C]">{p.category} · requirement: {reqLabel(p)}{p.comment ? ` · ${p.comment}` : ''}</p>
                  </div>
                  {(p.requirement_type === 'range' || p.requirement_type === 'max') ? (
                    <Input
                      type="number" step="any"
                      value={form.entries[p.id]?.value ?? ''}
                      onChange={(e) => setEntry(p.id, { value: e.target.value })}
                      placeholder="Reading"
                      className="h-8 text-sm font-mono w-[120px] shrink-0"
                      data-testid={`qc-value-${p.id}`}
                    />
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                      <Checkbox
                        checked={!!form.entries[p.id]?.detected}
                        onCheckedChange={(v) => setEntry(p.id, { detected: !!v })}
                        data-testid={`qc-detected-${p.id}`}
                      />
                      <span className="text-xs font-semibold text-[#78716C]">Detected</span>
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)} className="border-[#EDE6D8] rounded-lg" data-testid="qc-dialog-cancel">Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
              data-testid="qc-dialog-save"
            >
              {saving ? <Spinner size={14} className="animate-spin mr-2" /> : <Plus size={14} className="mr-2" />}
              Save Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View report dialog */}
      <Dialog open={!!viewTest} onOpenChange={(o) => { if (!o) setViewTest(null); }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="qc-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Report {viewTest?.report_number || ''}
              {viewTest && <StatusChip status={viewTest.overall_pass ? 'pass' : 'fail'} label={viewTest.overall_pass ? 'PASS' : `FAIL (${viewTest.failed_count})`} />}
            </DialogTitle>
            <DialogDescription>
              {viewTest && `${viewTest.store_name || 'Store'} · ${fmtDate(viewTest.sample_date)} · ${viewTest.tested_by}`}
            </DialogDescription>
          </DialogHeader>
          {viewTest && (
            <div className="border border-[#EDE6D8] rounded-lg divide-y divide-[#EDE6D8]">
              {(viewTest.results || []).map((r, i) => (
                <div key={i} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1C1917] truncate">{r.parameter}</p>
                    <p className="text-[10px] font-mono text-[#78716C]">requirement: {reqLabel(r)}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-mono text-[#0A2540]">
                      {(r.requirement_type === 'range' || r.requirement_type === 'max')
                        ? r.value
                        : (r.detected ? 'Detected' : 'Not detected')}
                    </span>
                    <StatusChip status={r.passed ? 'pass' : 'fail'} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {viewTest?.comment && <p className="text-xs text-[#78716C]">Note: {viewTest.comment}</p>}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => printQualityReport(viewTest)}
              className="border-[#EDE6D8] rounded-lg"
              data-testid="qc-print-btn"
            >
              <Printer size={14} className="mr-2" /> Print Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
